class SizePreferenceModal {
  constructor() {
    this.modal = document.querySelector('.size-modal');
    this.modalTriggers = document.querySelectorAll('[data-open-size-modal]');
    this.closeButton = document.querySelector('.size-modal__close');
    this.clearButton = document.querySelector('#clear-preferences');
    this.overlay = document.querySelector('.size-modal__overlay');
    this.form = document.querySelector('#size-preferences-form');
    this.previousActiveElement = null;

    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleFormKeyDown = this.handleFormKeyDown.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.updateStickyHeightVar = this.updateStickyHeightVar.bind(this);

    this.init();
  }

  init() {
    this.modalTriggers.forEach(trigger => {
      trigger.addEventListener('click', this.openModal);
    });
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.closeModal);
    }
    if (this.clearButton) {
      this.clearButton.addEventListener('click', this.clearPreferences.bind(this));
    }
    if (this.overlay) {
      this.overlay.addEventListener('click', this.closeModal);
    }
    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit);
    }
    if (this.form) {
      this.form.addEventListener('keydown', this.handleFormKeyDown);
    }
    document.addEventListener('keydown', this.handleKeyDown);
    this.checkExistingPreferences();
    this.updateLandingFilterState();
    this.setupStickySubstate();
    // Initialize the CSS variable so consumers can rely on it being defined
    // whether or not the sticky has ever been shown.
    this.updateStickyHeightVar();
    window.addEventListener('resize', this.updateStickyHeightVar);
  }

  // Scroll substate: the pinned copy of the active state shows once the inline
  // active state has scrolled up behind the header and hides again when it
  // comes back into view (or the preferences are cleared, which hides the
  // inline state). The threshold is the header's painted bottom edge
  // (--header-offset, sticky-stack.js), so the hand-off is seamless and a
  // section still below the fold never pins.
  setupStickySubstate() {
    this.stickyEl = document.querySelector('[data-landing-filter-sticky]');
    this.inlineActive = document.querySelector('[data-landing-filter-state="active"]');
    if (!this.stickyEl || !this.inlineActive) return;

    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        this.syncSticky();
      });
    };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    document.addEventListener('sticky-stack:change', schedule);
    this.syncSticky();
  }

  isScrolledPastInline() {
    const headerBottom =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-offset')) || 0;
    return this.inlineActive.getBoundingClientRect().bottom <= headerBottom;
  }

  syncSticky() {
    if (!this.stickyEl || !this.inlineActive) return;
    const show = !this.inlineActive.hidden && this.isScrolledPastInline();
    if (show === !this.stickyEl.hidden) return;
    this.stickyEl.hidden = !show;
    this.updateStickyHeightVar();
  }

  // Publishes the visible height of the sticky landing-filter as a CSS
  // variable on :root so other sticky elements can offset themselves with
  // `calc(var(--header-offset) + var(--size-modal-height, 0px))` etc.
  // Resolves to 0px whenever the sticky is hidden or absent (because the
  // [hidden] attribute is styled `display: none !important`, offsetHeight
  // naturally returns 0 in that state).
  updateStickyHeightVar() {
    const sticky = this.stickyEl || document.querySelector('[data-landing-filter-sticky]');
    const height = sticky ? sticky.offsetHeight : 0;
    document.documentElement.style.setProperty('--size-modal-height', `${height}px`);
  }

  hasActivePreferences() {
    try {
      const stored = localStorage.getItem('sizePreferences');
      if (!stored) return false;
      const preferences = JSON.parse(stored);
      return this.isStorageValid(preferences) && this.hasAnySelections(preferences.selectedSizes);
    } catch (e) {
      return false;
    }
  }

  getAllOptionValues(selectName) {
    const select = this.form.querySelector(`[name="${selectName}"]`);
    if (!select) return [];
    return Array.from(select.options)
      .filter(opt => opt.value !== '')
      .map(opt => opt.value);
  }

  handleFormKeyDown(event) {
    if (event.key === 'Enter') {
      const active = document.activeElement;
      const tag = active.tagName;
      const type = active.type;
  
      // Only allow Enter to submit if the submit button is focused
      const isSubmitButton = tag === 'BUTTON' && type === 'submit' && this.form.contains(active);
  
      if (!isSubmitButton) {
        event.preventDefault();
        // For checkboxes/radios, manually toggle them on Enter
        if (tag === 'INPUT' && (type === 'checkbox' || type === 'radio')) {
          active.click();
        }
      }
    }
  }

  openModal() {
    this.previousActiveElement = document.activeElement;
    this.modal.removeAttribute('hidden');
    const focusableElements = this.modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusableElements.length) {
      focusableElements[0].focus();
    }
    document.body.style.overflow = 'hidden';
  }

  closeModal() {
    this.modal.setAttribute('hidden', '');
    if (this.previousActiveElement) {
      this.previousActiveElement.focus();
    }
    document.body.style.overflow = '';
  }

  handleKeyDown(event) {
    if (event.key === 'Escape' && !this.modal.hasAttribute('hidden')) {
      this.closeModal();
    }
    if (!this.modal.hasAttribute('hidden')) {
      const focusableElements = this.modal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements.length) {
        const firstFocusableElement = focusableElements[0];
        const lastFocusableElement = focusableElements[focusableElements.length - 1];
        if (event.key === 'Tab' && event.shiftKey && document.activeElement === firstFocusableElement) {
          event.preventDefault();
          lastFocusableElement.focus();
        }
        if (event.key === 'Tab' && !event.shiftKey && document.activeElement === lastFocusableElement) {
          event.preventDefault();
          firstFocusableElement.focus();
        }
      }
    }
  }

  showLoadingStates() {    
    // Find all dynamic sections that will be updated
    const dynamicSections = document.querySelectorAll('[data-dynamic-section]');
    
    dynamicSections.forEach(section => {
      this.addLoadingStateToSection(section);
    });
  }
  
  hideLoadingStates() {    
    // Remove loading states from all dynamic sections
    const dynamicSections = document.querySelectorAll('[data-dynamic-section]');
    
    dynamicSections.forEach(section => {
      this.removeLoadingStateFromSection(section);
    }); 
  }
  
  addLoadingStateToSection(section) {
    // Add loading class to section
    section.classList.add('loading');
    
    // Create loading overlay if it doesn't exist
    if (!section.querySelector('.loading-overlay')) {
      const overlay = this.createLoadingOverlay();
      section.appendChild(overlay);
    }
    
    // Add loading class to child collections
    const childCollections = section.querySelectorAll('[data-child-collection]');
    childCollections.forEach(child => {
      child.classList.add('loading');
    });
  }
  
  removeLoadingStateFromSection(section) {
    // Remove loading class
    section.classList.remove('loading');
    
    // Remove loading overlay
    const overlay = section.querySelector('.loading-overlay');
    if (overlay) {
      overlay.remove();
    }
    
    // Remove loading from child collections
    const childCollections = section.querySelectorAll('[data-child-collection]');
    childCollections.forEach(child => {
      child.classList.remove('loading');
    });
  }
  
  createLoadingOverlay() {
    const overlay = document.createElement('div');
    overlay.className = 'loading-overlay';
    
    // Create spinner
    const spinner = document.createElement('div');
    spinner.className = 'loading-spinner';
    
    // Create loading text
    const text = document.createElement('div');
    text.className = 'loading-text';
    text.textContent = 'Updating products...';
    
    overlay.appendChild(spinner);
    overlay.appendChild(text);
    
    return overlay;
  }

  handleSubmit(event) {
    event.preventDefault();

    const formData = new FormData(this.form);
    let bottomSizes = formData.getAll('bottom_sizes').filter(v => v !== '');
    let inseamSizes = formData.getAll('inseam_sizes').filter(v => v !== '');

    // If only inseam is selected, use all bottom sizes (and vice versa)
    if (inseamSizes.length && !bottomSizes.length) {
      bottomSizes = this.getAllOptionValues('bottom_sizes');
    }
    if (bottomSizes.length && !inseamSizes.length) {
      inseamSizes = this.getAllOptionValues('inseam_sizes');
    }

    let bottomInseamCombos = [];
    if (bottomSizes.length && inseamSizes.length) {
      bottomSizes.forEach(bottom => {
        inseamSizes.forEach(inseam => {
          bottomInseamCombos.push(`${bottom}x${inseam}`);
        });
      });
    }

    const selectedSizes = {
      top_sizes: formData.getAll('top_sizes').filter(v => v !== ''),
      bottom_sizes: bottomInseamCombos,
      shoe_sizes: formData.getAll('shoe_sizes').filter(v => v !== ''),
      preferred_fits: []
    };

    const allBottomSizes = this.getAllOptionValues('bottom_sizes');
    const allInseamSizes = this.getAllOptionValues('inseam_sizes');
    let allBottomInseamCombos = [];
    if (allBottomSizes.length && allInseamSizes.length) {
      allBottomSizes.forEach(bottom => {
        allInseamSizes.forEach(inseam => {
          allBottomInseamCombos.push(`${bottom}x${inseam}`);
        });
      });
    }

    const allTypeValues = {
      top_sizes: this.getAllOptionValues('top_sizes'),
      bottom_sizes: allBottomInseamCombos,
      inseam_sizes: allInseamSizes,
      shoe_sizes: this.getAllOptionValues('shoe_sizes'),
      preferred_fits: [],
    };

    const rawSelections = {
      top_sizes: formData.get('top_sizes') || '',
      waist: formData.get('bottom_sizes') || '',
      inseam: formData.get('inseam_sizes') || '',
      shoe_sizes: formData.get('shoe_sizes') || ''
    };

    if (Object.keys(selectedSizes).some(key => selectedSizes[key].length > 0)) {
      const storageData = {
        selectedSizes,
        allTypeValues,
        rawSelections,
        timestamp: Date.now(),
        version: '1.0'
      };

      try {
        localStorage.setItem('sizePreferences', JSON.stringify(storageData));
      } catch (error) {
        console.warn('Failed to save preferences to local storage:', error);
      }
    } else {
      localStorage.removeItem('sizePreferences');
    }

    this.updateProductSections(selectedSizes, allTypeValues);
    this.updateLandingFilterState();
    this.closeModal();
  }

  checkExistingPreferences() {
    try {
      const stored = localStorage.getItem('sizePreferences');
      if (stored) {
        const preferences = JSON.parse(stored);
        
        // Check if storage is still valid (optional: add expiration)
        if (this.isStorageValid(preferences)) {
          this.updateProductSections(preferences.selectedSizes, preferences.allTypeValues);
          this.populateFormWithPreferences(preferences);
        } else {
          // Clear expired storage and revert to original content
          localStorage.removeItem('sizePreferences');
          this.revertToOriginalContent();
        }
      }
    } catch (error) {
      console.warn('Failed to load stored preferences:', error);
      localStorage.removeItem('sizePreferences');
      this.revertToOriginalContent();
    }
  }
  
  isStorageValid(preferences) {
    // Optional: Add expiration logic
    const MAX_AGE_HOURS = 24;
    const age = Date.now() - preferences.timestamp;
    return age < (MAX_AGE_HOURS * 60 * 60 * 1000);
  }
  
  populateFormWithPreferences(preferences) {
    if (!preferences) return;

    // Prefer the raw, single-select values when available
    const raw = preferences.rawSelections;
    if (raw) {
      const topSelect = this.form.querySelector('[name="top_sizes"]');
      if (topSelect) topSelect.value = raw.top_sizes || '';

      const waistSelect = this.form.querySelector('[name="bottom_sizes"]');
      if (waistSelect) waistSelect.value = raw.waist || '';

      const inseamSelect = this.form.querySelector('[name="inseam_sizes"]');
      if (inseamSelect) inseamSelect.value = raw.inseam || '';

      const shoeSelect = this.form.querySelector('[name="shoe_sizes"]');
      if (shoeSelect) shoeSelect.value = raw.shoe_sizes || '';
      return;
    }

    // Fallback for legacy storage that lacks rawSelections
    const sizes = preferences.selectedSizes;
    if (!sizes || typeof sizes !== 'object') return;

    const setSelect = (name, value) => {
      const select = this.form.querySelector(`[name="${name}"]`);
      if (select) select.value = value || '';
    };

    setSelect('top_sizes', sizes.top_sizes?.[0]);
    setSelect('shoe_sizes', sizes.shoe_sizes?.[0]);

    const combo = sizes.bottom_sizes?.[0];
    if (combo && combo.includes('x')) {
      const [waist, inseam] = combo.split('x');
      setSelect('bottom_sizes', waist);
      setSelect('inseam_sizes', inseam);
    }
  }

  clearPreferences() {
    // Clear local storage
    localStorage.removeItem('sizePreferences');
    
    // Reset form
    this.form.reset();
    
    // Update product sections to show all products (no filters)
    // Pass null to indicate we want to clear all filters and show original content
    this.updateProductSections(null, null);
    
    // Show success message
    this.showClearMessage();
    
    // Close modal
    this.closeModal();
  }

  showClearMessage() {
    // Create temporary success message
    const message = document.createElement('div');
    message.className = 'size-modal__message size-modal__message--success';
    message.textContent = 'Preferences cleared successfully';
    
    this.modal.querySelector('.size-modal__content').appendChild(message);
    
    // Remove after 3 seconds
    setTimeout(() => {
      message.remove();
    }, 3000);
  }

  // --- Landing filter visual state ---

  updateLandingFilterState() {
    const defaultState = document.querySelector('[data-landing-filter-state="default"]');
    const activeState = document.querySelector('[data-landing-filter-state="active"]');

    if (!defaultState || !activeState) return;

    try {
      const stored = localStorage.getItem('sizePreferences');
      if (stored) {
        const preferences = JSON.parse(stored);
        if (this.isStorageValid(preferences) && this.hasAnySelections(preferences.selectedSizes)) {
          defaultState.setAttribute('hidden', '');
          activeState.removeAttribute('hidden');
          this.populateSizeDisplay(preferences);
          this.syncSticky();
          return;
        }
      }
    } catch (e) {
      console.warn('Error reading preferences for filter state:', e);
    }

    defaultState.removeAttribute('hidden');
    activeState.setAttribute('hidden', '');
    this.syncSticky();
  }

  hasAnySelections(selectedSizes) {
    if (!selectedSizes) return false;
    return Object.values(selectedSizes).some(v => Array.isArray(v) && v.length > 0);
  }

  populateSizeDisplay(preferences) {
    const setText = (attr, value) => {
      document.querySelectorAll(`[data-size-value="${attr}"]`).forEach(el => {
        el.textContent = value || '–';
      });
    };

    const raw = preferences.rawSelections;
    if (raw) {
      setText('top_sizes', raw.top_sizes);
      setText('waist', raw.waist);
      setText('inseam', raw.inseam);
      setText('shoe_sizes', raw.shoe_sizes);
      return;
    }

    const sizes = preferences.selectedSizes;
    setText('top_sizes', sizes.top_sizes?.[0]);
    setText('shoe_sizes', sizes.shoe_sizes?.[0]);

    const combo = sizes.bottom_sizes?.[0];
    setText('waist', combo?.includes('x') ? combo.split('x')[0] : '');
    setText('inseam', combo?.includes('x') ? combo.split('x')[1] : '');
  }

  // --- Product section updates (unchanged filtering logic) ---

  revertToOriginalContent() {
    const dynamicSections = document.querySelectorAll('[data-dynamic-section]');
    
    if (dynamicSections.length === 0) {
      // Hide loading states before reloading
      this.hideLoadingStates();
      // Fallback: reload the current page without any size filters
      this.reloadPageWithoutFilters();
      return;
    }
    
    let hasSuccessfulUpdates = false;
    let totalSections = 0;
    let completedSections = 0;
    
    // Count total sections to process
    dynamicSections.forEach(section => {
      if (section.hasAttribute('data-multi-collection-section')) {
        totalSections += section.querySelectorAll('[data-child-collection]').length;
      } else {
        totalSections += 1;
      }
    });
    
    // Fetch the original unfiltered collection pages for all dynamic sections
    dynamicSections.forEach(section => {
      if (section.hasAttribute('data-multi-collection-section')) {
        const childCollections = section.querySelectorAll('[data-child-collection]');
        
        childCollections.forEach(child => {
          const collectionHandle = child.dataset.childCollectionHandle;
          const collectionLimit = parseInt(child.dataset.childCollectionLimit, 10) || 8;
          
          // Fetch original collection without any filters
          const originalUrl = `${window.location.origin}/collections/${collectionHandle}`;
          
          fetch(originalUrl)
            .then(res => {
              if (!res.ok) {
                throw new Error(`HTTP error! status: ${res.status}`);
              }
              return res.text();
            })
            .then(html => {
              const doc = new DOMParser().parseFromString(html, 'text/html');
              let productCards = [...doc.querySelectorAll('.product-card-wrapper')];
              
              if (collectionLimit) {
                productCards = productCards.slice(0, collectionLimit);
              }
              
              this.updateSectionContent(child, productCards, section);
              hasSuccessfulUpdates = true;
              completedSections++;
              
              // Check if all sections are completed
              if (completedSections >= totalSections) {
                this.checkRevertSuccess(hasSuccessfulUpdates);
              }
            })
            .catch(error => {
              console.error(`Failed to revert collection content for ${collectionHandle}:`, error);
              completedSections++;
              
              // Check if all sections are completed
              if (completedSections >= totalSections) {
                this.checkRevertSuccess(hasSuccessfulUpdates);
              }
            });
        });
      } else {
        // Single-collection section
        const collectionHandle = section.dataset.sectionCollectionHandle;
        const collectionLimit = section.dataset.sectionCollectionLimit;
        
        // Fetch original collection without any filters
        const originalUrl = `${window.location.origin}/collections/${collectionHandle}`;
        
        fetch(originalUrl)
          .then(res => {
            if (!res.ok) {
              throw new Error(`HTTP error! status: ${res.status}`);
            }
            return res.text();
          })
          .then(html => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            let productCards = [...doc.querySelectorAll('.product-card-wrapper')];
            
            if (collectionLimit) {
              productCards = productCards.slice(0, collectionLimit);
            }
            
            this.updateSectionContent(section, productCards, section);
            hasSuccessfulUpdates = true;
            completedSections++;
            
            // Check if all sections are completed
            if (completedSections >= totalSections) {
              this.checkRevertSuccess(hasSuccessfulUpdates);
            }
          })
          .catch(error => {
            console.error(`Failed to revert collection content for ${collectionHandle}:`, error);
            completedSections++;
            
            // Check if all sections are completed
            if (completedSections >= totalSections) {
              this.checkRevertSuccess(hasSuccessfulUpdates);
            }
          });
      }
    });
  }

  checkRevertSuccess(hasSuccessfulUpdates) {
    // Always hide loading states when revert operation completes
    this.hideLoadingStates();
    
    if (!hasSuccessfulUpdates) {
      this.reloadPageWithoutFilters();
    }
  }

  reloadPageWithoutFilters() {
    // Remove any size-related URL parameters and reload
    const currentUrl = new URL(window.location.href);
    const sizeParams = [
      'filter.v.option.top+size',
      'filter.v.option.bottom+size', 
      'filter.v.option.inseam+size',
      'filter.v.option.shoe+size'
    ];
    
    sizeParams.forEach(param => {
      currentUrl.searchParams.delete(param);
    });
    
    // Reload the page with clean URL
    window.location.href = currentUrl.toString();
  }

  updateSectionContent(container, productCards, section) {
    // Update content based on container type
    if (container.querySelector('.swiper-wrapper')) {
      const swiperWrapper = container.querySelector('.swiper-wrapper');
      const slides = productCards.map(card => `<div class="swiper-slide">${card.outerHTML}</div>`).join('');
      swiperWrapper.innerHTML = slides;
      if (window.reinitSwipers) {
        window.reinitSwipers();
      }
    } else if (container.querySelector('.collection-grid')) {
      const collectionGrid = container.querySelector('.collection-grid');
      const items = productCards.map(card => `<div class="collection-grid__item">${card.outerHTML}</div>`).join('');
      collectionGrid.innerHTML = items;
    } else {
      const cardContainer = container.querySelector('.product-cards-container');
      if (cardContainer) {
        cardContainer.innerHTML = productCards.map(card => card.outerHTML).join('');
        if (window.reinitSwipers) {
          window.reinitSwipers();
        }
      }
    }
    
    // Handle no-products visibility
    if (productCards.length === 0) {
      container.classList.add('no-products__hidden');
      if (section && container.dataset.childCollectionHandle) {
        const tab = section.querySelector(`[data-multi-collection-tab][data-tab-collection-handle="${container.dataset.childCollectionHandle}"]`);
        if (tab) tab.classList.add('no-products__hidden');
      }
    } else {
      container.classList.remove('no-products__hidden');
      if (section && container.dataset.childCollectionHandle) {
        const tab = section.querySelector(`[data-multi-collection-tab][data-tab-collection-handle="${container.dataset.childCollectionHandle}"]`);
        if (tab) tab.classList.remove('no-products__hidden');
      }
    }
  }

  updateProductSections(filters, allTypeValues) {
    // Show loading states before starting updates
    this.showLoadingStates();

    // If filters is null, we're clearing preferences - revert to original content
    if (filters === null) {
      this.revertToOriginalContent();
      return;
    }
    
    const collectionTypeToParams = {
      'shop all tops': ['top_sizes'],
      'all tops': ['top_sizes'],
      'shop all bottoms': ['bottom_sizes', 'inseam_sizes'],
      'all bottoms': ['bottom_sizes', 'inseam_sizes'],
      'shop all footwear': ['shoe_sizes'],
      'all footwear': ['shoe_sizes'],
      'footwear': ['shoe_sizes'],
    };
    

    function buildCollectionQuery(collectionHandle, filters, type, allTypeValues, forceAllForType) {
      const params = new URLSearchParams();
      params.set('sort_by', 'best-selling');
      params.set('filter.v.price.gte', '');
      params.set('filter.v.availability', '1');
      const relevantParams = collectionTypeToParams[type] || [];
      function mapSize(size) {
        if (size === 'S') return 'Small';
        if (size === 'M') return 'Medium';
        if (size === 'L') return 'Large';
        if (size === '2XL') return 'XXL';
        if (size === '3XL') return 'XXXL';
        return size;
      }
      relevantParams.forEach(param => {
        let values = filters && filters[param];
        if ((!values || values.length === 0) && forceAllForType && allTypeValues) {
          values = allTypeValues[param] || [];
        }
        if (!values || values.length === 0) {
          return; // skip if still empty
        }
    
        if (param === 'top_sizes' || param === 'bottom_sizes') {
          values = values.map(mapSize);
        }
        values.forEach(size => {
          if (param === 'top_sizes') params.append('filter.v.option.top+size', size);
          if (param === 'bottom_sizes') params.append('filter.v.option.bottom+size', size);
          if (param === 'inseam_sizes') params.append('filter.v.option.inseam+size', size);
          if (param === 'shoe_sizes') params.append('filter.v.option.shoe+size', size);
        });
      });
      return `${window.location.origin}/collections/${collectionHandle}?${params.toString().replace(/%2B/g, '+')}`;
    }

    const updatePromises = [];

    document.querySelectorAll('[data-dynamic-section]').forEach(section => {
      if (section.hasAttribute('data-multi-collection-section')) {
        section.querySelectorAll('[data-child-collection]').forEach(child => {
          const collectionHandle = child.dataset.childCollectionHandle;
          const collectionLimit = parseInt(child.dataset.childCollectionLimit, 10) || 8;
          const matchingCollections = child.dataset.childMatchingCollections;

          // Skip if no matching collections
          if (!matchingCollections || !matchingCollections.trim()) {
            return;
          }

          const typesToQuery = matchingCollections.split(',').map(s => s.trim()).filter(Boolean);

          if (typesToQuery.length === 1) {
            // Only one type, just run one query
            const type = typesToQuery[0];
            const url = buildCollectionQuery(collectionHandle, filters, type, allTypeValues, false);
            const promise = fetch(url)
              .then(res => res.text())
              .then(html => {
                const doc = new DOMParser().parseFromString(html, 'text/html');
                let productCards = [...doc.querySelectorAll('.product-card-wrapper')];
                if (collectionLimit) {
                  productCards = productCards.slice(0, collectionLimit);
                }
                this.updateSectionContent(child, productCards, section);
              });
            updatePromises.push(promise);
          } else {
            // Multiple types: run all queries and combine results up to the limit
            const promises = typesToQuery.map(type => {
                const url = buildCollectionQuery(collectionHandle, filters, type, allTypeValues, true);
                return fetch(url)
                  .then(res => res.text())
                  .then(html => {
                    const doc = new DOMParser().parseFromString(html, 'text/html');
                    return [...doc.querySelectorAll('.product-card-wrapper')];
                  });
              });
            const combinedPromise = Promise.all(promises)
            .then(resultsArrays => {
              const allCards = [].concat(...resultsArrays);
              const seen = new Set();
              const uniqueCards = allCards.filter(card => {
                const handle = card.getAttribute('data-product-handle') || card.dataset.productHandle || card.id;
                if (seen.has(handle)) return false;
                seen.add(handle);
                return true;
              });
              const productCards = uniqueCards.slice(0, collectionLimit);
              this.updateSectionContent(child, productCards, section);
            });
            updatePromises.push(combinedPromise);
          }
        });
      } else {
        // Single-collection section (same as before, but use /collections/)
        const collectionHandle = section.dataset.sectionCollectionHandle;
        const collectionLimit = section.dataset.sectionCollectionLimit;
        const matchingCollections = section.dataset.sectionMatchingCollections;
        let typesToQuery = [];
        if (matchingCollections && matchingCollections.trim()) {
          typesToQuery = matchingCollections.split(',').map(s => s.trim()).filter(Boolean);
        } else {
          typesToQuery = [collectionHandle]; // fallback
        }
        const type = typesToQuery[0];
        const url = buildCollectionQuery(collectionHandle, filters, type);
        const promise = fetch(url)
          .then(res => res.text())
          .then(html => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            let productCards = [...doc.querySelectorAll('.product-card-wrapper')];
            if (collectionLimit) {
              productCards = productCards.slice(0, collectionLimit);
            }
            this.updateSectionContent(section, productCards, section);
          });
        updatePromises.push(promise);
      }
    });
    Promise.allSettled(updatePromises)
      .then(results => {
        this.hideLoadingStates();
        
        // Check if any updates failed
        const failedUpdates = results.filter(result => result.status === 'rejected');
        if (failedUpdates.length > 0) {
          console.warn(`${failedUpdates.length} updates failed`);
          this.showUpdateSummary(failedUpdates.length, results.length);
        }
      })
      .catch(error => {
        console.error('Error in update process:', error);
        this.hideLoadingStates();
      });
  }
}

// Initialize the modal when the DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('.size-modal')) {
    new SizePreferenceModal();
  }
});